import config from 'config';

import { axiosClient } from 'axiosClient/axiosClient';
import { StatusBE } from 'components/detailsModal/models';

export type CorporateAccountDetailsData = {
  companyName: string;
  website: string | null;
  businessSector: string | null;
  country: string | null;
  logoUrl: string | null;
};

export type CorporateAccountPrimaryContactData = {
  firstName: string | null;
  lastName: string | null;
  role: string | null;
  email: string | null;
  mobileNumber: string | null;
  mobileNumberCode: string | null;
};

export interface CorporateAccount extends CorporateAccountDetailsData {
  id: string;
  status: StatusBE;
  createdDate: string;
  primaryContact: CorporateAccountPrimaryContactData | null;
}

export type PageInfo = {
  page: number;
  pageSize: number;
  sortBy: string;
  search: string;
};

type CorporateAccountsPayload = {
  data: PageInfo;
};

export type CorporateAccountsResponse = {
  page: number;
  pageSize: number;
  totalCount: number;
  items: CorporateAccount[];
};

export const getCorporateAccounts = ({
  data,
}: CorporateAccountsPayload): Promise<CorporateAccountsResponse> =>
  axiosClient
    .post(`/api/v${config.apiVersion}/CorporateAccounts/GetPage`, data)
    .then((response) => response.data.payload.corporateAccounts);

interface CorporateAccountForCreation extends CorporateAccountDetailsData {
  primaryContact: CorporateAccountPrimaryContactData | null;
}

export type CreateCorporateAccountPayload = {
  groupName: string;
  businessSector: string | null;
  logoFile: File | null;
};

export type CreateCorporateAccountParams = {
  groupName: string;
  businessSector: string | null;
};

export const createCorporateAccount = async ({
  groupName,
  businessSector,
}: CreateCorporateAccountParams) => {
  const data: CorporateAccountForCreation = {
    companyName: groupName,
    businessSector: businessSector ?? null,
    country: null,
    website: null,
    logoUrl: null,
    primaryContact: null,
  };

  const response = await axiosClient.post(
    `/api/v${config.apiVersion}/CorporateAccounts`,
    data
  );

  return response.data.payload;
};

type DeleteCorporateAccountPayload = {
  corporateAccountId: string;
};

export const deleteCorporateAccount = ({
  corporateAccountId,
}: DeleteCorporateAccountPayload) =>
  axiosClient.delete(
    `/api/v${config.apiVersion}/CorporateAccounts/${corporateAccountId}`
  );

type SuspendCorporateAccountPayload = {
  corporateAccountId: string;
};

export const suspendCorporateAccount = ({
  corporateAccountId,
}: SuspendCorporateAccountPayload) =>
  axiosClient.put(
    `/api/v${config.apiVersion}/CorporateAccounts/${corporateAccountId}/Suspend`,
    null
  );

type ActivateCorporateAccountPayload = {
  corporateAccountId: string;
};

export const activateCorporateAccount = ({
  corporateAccountId,
}: ActivateCorporateAccountPayload) =>
  axiosClient.put(
    `/api/v${config.apiVersion}/CorporateAccounts/${corporateAccountId}/Activate`,
    null
  );

export type EditCorporateAccountPayload = {
  data: CorporateAccount;
};

export const editCorporateAccount = ({ data }: EditCorporateAccountPayload) =>
  axiosClient.put(
    `/api/v${config.apiVersion}/CorporateAccounts/${data.id}`,
    data
  );

type GetCorporateAccount = {
  corporateAccountId: string;
};

export const getCorporateAccount = ({
  corporateAccountId,
}: GetCorporateAccount) =>
  axiosClient.get(
    `/api/v${config.apiVersion}/CorporateAccounts/${corporateAccountId}`
  );

type UploadAccountLogoPayload = {
  id: string;
  formData: FormData;
};

export const uploadAccountLogo = async ({
  id,
  formData,
}: UploadAccountLogoPayload) => {
  const response = await axiosClient.put(
    `/api/v${config.apiVersion}/CorporateAccounts/${id}/UploadLogo`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
    }
  );

  return response.data.payload;
};
